import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Menu,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  Warning as DelayedIcon,
  Cancel as CancelledIcon,
  Assignment as TaskIcon,
  Event as EventIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';

interface TimelineEvent {
  id: number;
  title: string;
  description: string;
  date: string;
  type: 'milestone' | 'task' | 'meeting' | 'deadline' | 'event';
  status: 'completed' | 'pending' | 'delayed' | 'cancelled';
  project: string;
  assignee?: string;
  priority?: 'high' | 'medium' | 'low';
}

/**
 * Project Timeline Component for Project Manager
 * Displays project milestones, tasks, and events in chronological order
 */
const ProjectTimeline: React.FC = () => {
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterProject, setFilterProject] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);

  const projects = ['E-commerce Platform Redesign', 'Mobile App Development', 'Dashboard Analytics Implementation'];

  useEffect(() => {
    loadTimelineData();
  }, []);

  const loadTimelineData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Mock data for development - replace with actual API call
      const mockTimelineEvents: TimelineEvent[] = [
        {
          id: 1,
          title: "Project Kickoff Meeting",
          description: "Initial meeting with client to discuss requirements and project scope",
          date: "2024-03-15T10:00:00Z",
          type: "meeting",
          status: "completed",
          project: "E-commerce Platform Redesign",
          assignee: "Project Team"
        },
        {
          id: 2,
          title: "UI/UX Design Completion",
          description: "Complete wireframes and mockups for the new e-commerce interface",
          date: "2024-03-20T17:00:00Z",
          type: "milestone",
          status: "completed",
          project: "E-commerce Platform Redesign",
          assignee: "Emily Davis",
          priority: "high"
        },
        {
          id: 3,
          title: "Backend API Development",
          description: "Develop REST APIs for product catalog and user authentication",
          date: "2024-03-25T12:00:00Z",
          type: "task",
          status: "pending",
          project: "E-commerce Platform Redesign",
          assignee: "David Kim",
          priority: "high"
        },
        {
          id: 4,
          title: "Mobile App Beta Release",
          description: "Deploy beta version of mobile app for client testing",
          date: "2024-03-28T15:30:00Z",
          type: "milestone",
          status: "pending",
          project: "Mobile App Development",
          assignee: "Mike Chen",
          priority: "medium"
        },
        {
          id: 5,
          title: "Client Feedback Review",
          description: "Review and document client feedback from beta testing phase",
          date: "2024-04-02T14:00:00Z",
          type: "meeting",
          status: "pending",
          project: "Mobile App Development",
          assignee: "Sarah Johnson"
        },
        {
          id: 6,
          title: "Analytics Dashboard Launch",
          description: "Final deployment of analytics dashboard to production",
          date: "2024-04-05T16:00:00Z",
          type: "deadline",
          status: "pending",
          project: "Dashboard Analytics Implementation",
          assignee: "Alex Rodriguez",
          priority: "high"
        },
        {
          id: 7,
          title: "Performance Testing",
          description: "Conduct comprehensive performance testing across all modules",
          date: "2024-04-08T11:00:00Z",
          type: "task",
          status: "pending",
          project: "E-commerce Platform Redesign",
          assignee: "Sarah Johnson",
          priority: "medium"
        },
        {
          id: 8,
          title: "Security Audit",
          description: "Third-party security audit of the completed application",
          date: "2024-04-12T09:00:00Z",
          type: "event",
          status: "pending",
          project: "E-commerce Platform Redesign",
          priority: "high"
        }
      ];
      
      setTimelineEvents(mockTimelineEvents);
    } catch (err) {
      setError('Failed to load timeline data. Please try again.');
      console.error('Error loading timeline data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, timelineEvent: TimelineEvent) => {
    setAnchorEl(event.currentTarget);
    setSelectedEvent(timelineEvent);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CompletedIcon color="success" />;
      case 'pending': return <PendingIcon color="primary" />;
      case 'delayed': return <DelayedIcon color="warning" />;
      case 'cancelled': return <CancelledIcon color="error" />;
      default: return <PendingIcon />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'milestone': return <CompletedIcon />;
      case 'task': return <TaskIcon />;
      case 'meeting': return <PersonIcon />;
      case 'deadline': return <DelayedIcon />;
      case 'event': return <EventIcon />;
      default: return <TaskIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'primary';
      case 'delayed': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const filteredEvents = timelineEvents.filter(event => {
    const projectMatch = filterProject === 'all' || event.project === filterProject;
    const statusMatch = filterStatus === 'all' || event.status === filterStatus;
    return projectMatch && statusMatch;
  });

  // Sort events by date (newest first)
  const sortedEvents = [...filteredEvents].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Project Timeline
        </Typography>
        <Button variant="contained" startIcon={<EventIcon />}>
          Add Event
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={loadTimelineData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <FilterIcon color="action" />
            <Typography variant="subtitle1">Filters:</Typography>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={filterProject}
                label="Project"
                onChange={(e) => setFilterProject(e.target.value)}
              >
                <MenuItem value="all">All Projects</MenuItem>
                {projects.map(project => (
                  <MenuItem key={project} value={project}>{project}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="delayed">Delayed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            {(filterProject !== 'all' || filterStatus !== 'all') && (
              <Button 
                size="small" 
                onClick={() => {
                  setFilterProject('all');
                  setFilterStatus('all');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {sortedEvents.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <EventIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Timeline Events
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filterProject !== 'all' || filterStatus !== 'all' 
                  ? 'No events match your current filters.' 
                  : 'No timeline events have been created yet.'
                }
              </Typography>
              <Button variant="contained" sx={{ mt: 2 }} startIcon={<EventIcon />}>
                Create First Event
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <List sx={{ width: '100%' }}>
              {sortedEvents.map((event, index) => (
                <React.Fragment key={event.id}>
                  <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                    <ListItemIcon sx={{ mt: 1 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: `${getStatusColor(event.status)}.main`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        {getTypeIcon(event.type)}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="start" sx={{ mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" component="h3">
                              {event.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {event.description}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Chip 
                                label={event.status} 
                                size="small" 
                                color={getStatusColor(event.status) as any}
                              />
                              <Chip 
                                label={event.type} 
                                size="small" 
                                variant="outlined"
                              />
                              {event.priority && (
                                <Chip 
                                  label={event.priority.toUpperCase()} 
                                  size="small" 
                                  color={getPriorityColor(event.priority) as any}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                          <IconButton 
                            size="small"
                            onClick={(e) => handleMenuOpen(e, event)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      }
                      secondary={
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Project:</strong> {event.project}
                            </Typography>
                            {event.assignee && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Assignee:</strong> {event.assignee}
                              </Typography>
                            )}
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(event.date).date}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(event.date).time}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < sortedEvents.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Event Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <EventIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <TaskIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Event
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <PersonIcon sx={{ mr: 1 }} fontSize="small" />
          Reassign
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProjectTimeline;